import { useEffect, useRef, useState } from "react";
import { truncate } from "lodash";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

interface FileUploadComponentProps {
  errorMessage: string;
  name: string;
  onChange: (value: string) => void;
}

export const FileUploadComponent = ({
  name,
  errorMessage,
  onChange,
}: FileUploadComponentProps) => {
  const { error } = useToast();
  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (errorMessage) {
      error(errorMessage);
    }
  }, [errorMessage]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    // Read as data url
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result as string);
    };
    reader.readAsDataURL(file);
    setFileName(file.name);
  };

  const handleRemoveFile = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onChange("");
    setFileName(null);
  };

  return (
    <div>
      <label
        className={classNames(
          "bg-primary flex cursor-pointer rounded-md border border-gray-600 px-2 py-1.5 text-sm",
          {
            "justify-center": !fileName,
            "hover:bg-selector-active": !fileName,
            "border-red-500": errorMessage,
          },
        )}
      >
        {!fileName && (
          <span>{t("media_player.transcript_menu.file_type.user_file")}</span>
        )}
        {fileName && (
          <span className="flex gap-2">
            <Icon type="20-attach" size={20} className="shrink-0" />
            <span className="grow">
              {truncate(fileName, { length: 17, omission: "..." })}
            </span>
            <Icon
              type="16-close"
              size={16}
              onClick={handleRemoveFile}
              className="shrink-0"
            />
          </span>
        )}
        <input
          type="file"
          name={name}
          onChange={handleOnChange}
          className="hidden"
          itemType="text/vtt"
          accept=".vtt"
          ref={inputRef}
        />
      </label>
    </div>
  );
};
